import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "../ui";
import { useSession } from "./session";
import { LanguageSwitch } from "./LanguageSwitch";

export function LoginPage() {
  const { t } = useTranslation();
  const load = useSession((s) => s.load);
  const login = useSession((s) => s.session?.login);
  const loginError = new URLSearchParams(location.search).get("login_error");
  const [password, setPassword] = useState("");
  const [device, setDevice] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await fetch("/api/internal/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password, device: device || undefined }),
    });
    setBusy(false);
    if (res.ok) {
      await load();
      return;
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    setError(body.error ?? "error.generic");
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center px-6 py-16">
      <div className="mb-6 flex items-center gap-2.5">
        <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-accent text-[13px] font-bold tracking-[0.02em] text-white">
          Dr
        </span>
        <h1 className="text-[26px] font-semibold">{t("app.name")}</h1>
        <span className="ml-auto">
          <LanguageSwitch />
        </span>
      </div>
      <form onSubmit={submit} className="kss-surface flex flex-col gap-3 p-6" data-testid="login-form">
        <h2 className="text-[17px] font-semibold">{t("login.title")}</h2>
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-500">
          {t("login.password")}
          <Input
            type="password"
            name="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={error ? true : undefined}
          />
        </label>
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-500">
          {t("login.device")}
          <Input type="text" name="device" value={device} onChange={(e) => setDevice(e.target.value)} />
        </label>
        {error && (
          <p role="alert" className="text-[12.5px] text-err">
            {t(error)}
          </p>
        )}
        <Button type="submit" disabled={busy || !password || login?.password === false}>
          {t("login.submit")}
        </Button>
        {login?.password === false && (
          <p className="text-[12.5px] text-neutral-500">{t("login.passwordOff")}</p>
        )}
        {login?.oidc && (
          <a
            href="/api/internal/oidc/login"
            data-testid="oidc-login"
            className="inline-flex h-9 items-center justify-center rounded-sm border border-neutral-300 bg-[var(--surface)] px-3.5 text-[13.5px] font-medium hover:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            {t("login.sso")}
          </a>
        )}
        {loginError && (
          <p role="alert" className="text-[12.5px] text-err" data-testid="login-error">
            {t(`errors.${loginError}`, { defaultValue: t("error.generic") })}
          </p>
        )}
      </form>
    </div>
  );
}
